import React, { useEffect, useMemo, useState } from 'react';
import CommerceDateInput from '../CommerceDateInput';
import { createWebCreatorReservationRequest } from '../../../../api/webCreator';
import {
  buildReservationTimeSlots,
  earliestReservationDate,
  filterTimeSlotsForDate,
  formatPreferredDateIso,
  remainingCoversOnDay,
  dayKeyFromDate,
} from '../../utils/commerceDisplay';
import { isServiceDayOpen, weekdayKeyFromDate } from '../../utils/commerceAdmin';

export default function RestaurantExempleBooking({
  slug,
  site,
  reservation = {},
  bookingKicker,
  bookingTitle,
  interactive = true,
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState('12:00');
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resultHtml, setResultHtml] = useState('');
  const [showResult, setShowResult] = useState(false);

  const maxParty = Math.max(1, Number(reservation.maxPartySize) || 8);
  const leadTimeHours = Math.max(0, Number(reservation.leadTimeHours) || 2);
  const coversPerDay = Math.max(1, Number(reservation.coversPerDay) || 40);
  const dayBookings = site?.reservationDayBookings || {};
  const serviceWeek = site?.reservation?.serviceWeek;
  const minDate = useMemo(() => earliestReservationDate(leadTimeHours), [leadTimeHours]);

  const minDateIso = useMemo(() => {
    const y = minDate.getFullYear();
    const m = String(minDate.getMonth() + 1).padStart(2, '0');
    const d = String(minDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [minDate]);

  useEffect(() => {
    if (!selectedDate) setSelectedDate(minDate);
  }, [minDate, selectedDate]);

  const daySlot = useMemo(() => {
    if (!selectedDate || serviceWeek == null) return null;
    return serviceWeek[weekdayKeyFromDate(selectedDate)] || null;
  }, [selectedDate, serviceWeek]);

  const allSlots = useMemo(() => buildReservationTimeSlots(), []);
  const availableSlots = useMemo(
    () => filterTimeSlotsForDate(allSlots, selectedDate, leadTimeHours, daySlot),
    [allSlots, selectedDate, leadTimeHours, daySlot],
  );

  useEffect(() => {
    if (timeSlot && availableSlots.length && !availableSlots.includes(timeSlot)) {
      setTimeSlot(availableSlots[0] || '');
    }
  }, [timeSlot, availableSlots]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResult(false);
    if (!interactive || !slug) {
      setError('Réservation disponible sur le site publié.');
      return;
    }
    if (!consent) {
      setError('Veuillez accepter le traitement de vos données.');
      return;
    }
    if (!selectedDate || !timeSlot) {
      setError('Choisissez une date et un horaire.');
      return;
    }
    const dayKey = dayKeyFromDate(selectedDate);
    if (
      serviceWeek != null &&
      (!isServiceDayOpen(serviceWeek, selectedDate) ||
        remainingCoversOnDay(dayKey, coversPerDay, dayBookings, partySize) < partySize)
    ) {
      setError('Ce jour n’est pas disponible — choisissez une autre date.');
      return;
    }

    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    setBusy(true);
    try {
      await createWebCreatorReservationRequest(slug, {
        customerName: fullName,
        customerEmail: email.trim(),
        customerPhone: '',
        partySize,
        preferredDate: formatPreferredDateIso(selectedDate, timeSlot),
        note: note.trim(),
      });
      const dateLabel = new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(selectedDate);
      setResultHtml(
        `<strong>Merci ${escapeHtml(firstName.trim())} !</strong><br>
        Votre demande de table pour <strong>${partySize} ${partySize > 1 ? 'personnes' : 'personne'}</strong>,
        le <strong>${escapeHtml(dateLabel)}</strong> à <strong>${escapeHtml(timeSlot)}</strong> est enregistrée.<br>
        <span>Nous revenons vers vous rapidement pour confirmer.</span>`,
      );
      setShowResult(true);
    } catch (err) {
      setError(err?.message || 'Demande impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="booking-card" id="booking-form" onSubmit={submit}>
      <div className="booking-title">
        <div>
          <span className="booking-kicker">{bookingKicker}</span>
          <h3>{bookingTitle}</h3>
        </div>
        <span className="booking-icon">✳</span>
      </div>

      <div className="form-row">
        <label>
          Nombre de convives
          <select
            value={partySize}
            onChange={(e) => setPartySize(Number(e.target.value) || 1)}
            required
          >
            {Array.from({ length: maxParty }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n} personne{n > 1 ? 's' : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date souhaitée
          <CommerceDateInput
            value={selectedDate}
            min={minDateIso}
            onChange={setSelectedDate}
          />
        </label>
      </div>

      <fieldset>
        <legend>Choisissez votre service</legend>
        <div className="time-options">
          {(availableSlots.length ? availableSlots : allSlots.slice(0, 8)).map((slot) => (
            <label key={slot} className="time-option">
              <input
                type="radio"
                name="time"
                value={slot}
                checked={timeSlot === slot}
                onChange={() => setTimeSlot(slot)}
              />
              <span>{slot}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="form-row">
        <label>
          Votre prénom
          <input
            name="firstName"
            autoComplete="given-name"
            placeholder="Camille"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </label>
        <label>
          Votre nom
          <input
            name="lastName"
            autoComplete="family-name"
            placeholder="Martin"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </label>
      </div>

      <label>
        Votre e-mail
        <input
          name="email"
          type="email"
          autoComplete="email"
          placeholder="vous@exemple.fr"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      <label>
        Une précision ? <span className="optional">(facultatif)</span>
        <textarea
          name="notes"
          rows={2}
          placeholder="Allergies, occasion spéciale…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <label className="consent">
        <input
          type="checkbox"
          name="consent"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          J&apos;accepte que mes informations soient utilisées pour traiter ma demande de
          réservation.
        </span>
      </label>

      {error ? (
        <p className="booking-result" style={{ background: '#fde8e8', color: '#7f1d1d' }}>
          {error}
        </p>
      ) : null}

      <button className="button button-green booking-submit" type="submit" disabled={busy}>
        {busy ? 'Envoi…' : 'Demander ma réservation'}{' '}
        <span className="jardins-arrow-ne" aria-hidden="true" />
      </button>

      <p className="form-footnote">
        Demande transmise au restaurant — confirmation sous réserve de disponibilité.
      </p>

      <div
        className="booking-result"
        role="status"
        aria-live="polite"
        hidden={!showResult}
        dangerouslySetInnerHTML={showResult ? { __html: resultHtml } : undefined}
      />
    </form>
  );
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) =>
    ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char],
  );
}
